import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { FaArrowRight, FaPaperPlane, FaComments, FaTimes, FaUserTie } from "react-icons/fa";
import ReactMarkdown from "react-markdown";

const BotIcon = () => <FaUserTie size={14} />;
const ChatIcon = () => <FaComments size={22} />;
const CloseIcon = () => <FaTimes size={16} />;
const SendIcon = () => <FaPaperPlane size={14} />;
const NextIcon = () => <FaArrowRight size={12} />;

const SUGGESTIONS = ["How do I get my certificate?", "Explain how certificates work?",  ];
const PREVIOUS_MESSAGE_LIMIT = 12;
const INITIAL_ASSISTANT_TEXT =
  "Welcome to Evergreen Financial. I'm here to help with your financial counseling needs.";
const HEADER_CONTACT_CTA_TEXT = "Need help from our support team?";
const HEADER_CONTACT_CTA_HELPER_TEXT =
  "Click here to leave a message and a representative will contact you.";
const CONTACT_FORM_ALREADY_OPEN_TEXT =
  "You can fill out the form below to contact our support team.";
const CONTACT_FORM_SUBMITTED_TEXT =
  "We've received your details. A representative will reach out to you soon.";
const CONTACT_SUBMISSION_DISPLAY_TEXT = "Support request submitted";
const CONTACT_SUBMISSION_SUCCESS_TEXT =
  "Thank you for your message. Our support team will review it and contact you shortly.";
const CHAT_STORAGE_PREFIX = "aiChatWidget";
const LEGACY_CHAT_MESSAGES_KEY = "aiChatMessages";
const LEGACY_CHAT_SESSION_ID_KEY = "aiChatSessionId";
const DETAIL_FORM_INITIAL_STATE = {
  name: "",
  email: "",
  phoneNumber: "",
  message: "",
};
const DETAIL_PROMPT_TEXT = "Please choose Yes or No below.";
const DETAIL_FORM_PROMPT_TEXT =
  "Please share your contact details one step at a time. Our support team will review your message and contact you shortly.";

const DETAIL_FORM_STEPS = [
  {
    field: "name",
    label: "Name",
    prompt: "What is your name?",
    inputType: "text",
    autoComplete: "name",
    placeholder: "Enter your name",
  },
  {
    field: "email",
    label: "Email",
    prompt: "What email should support use?",
    inputType: "email",
    autoComplete: "email",
    placeholder: "Enter your email",
  },
  {
    field: "phoneNumber",
    label: "Phone",
    prompt: "What phone number can we reach you at?",
    inputType: "tel",
    autoComplete: "tel",
    placeholder: "Enter your phone number",
  },
  {
    field: "message",
    label: "Message",
    prompt: "How can our team help?",
    inputType: "textarea",
    autoComplete: "off",
    placeholder: "Type your message",
  },
];

const MARKDOWN_COMPONENTS = {
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        fontWeight: "bold",
        textDecoration: "underline",
        color: "inherit",
        cursor: "pointer",
        overflowWrap: "anywhere",
        wordBreak: "break-word",
      }}
    >
      {children}
    </a>
  ),
  strong: ({ children }) => <strong style={{ fontWeight: "bold" }}>{children}</strong>,
  ul: ({ children }) => <ul style={{ margin: "4px 0", paddingLeft: "20px" }}>{children}</ul>,
  li: ({ children }) => <li style={{ marginBottom: "4px" }}>{children}</li>,
  p: ({ children }) => <p style={{ margin: "0 0 8px", whiteSpace: "pre-wrap" }}>{children}</p>,
};

function now() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatUserDetail(user) {
  if (!user) return null;
  const data = user.data || user;
  const firstname = data.firstname || data.firstName || "";
  const lastname = data.lastname || data.lastName || "";
  const username = data.username || data.email || "";
  const email = data.email || "";
  const userType = data.type || data.userType || "";
  const userId = String(data.id ?? data.userId ?? "");
  const type = user?.isJoint && user.isJoint === true ? "joint" :  "Main";
// console.log("Formatted user detail:", {
//   username,
//   firstname,  lastname,
//   userType,
//   userId,       
//   email,
//   name: data.name || `${firstname} ${lastname}`.trim(),
//   type,
// });

  return {
    username,
    firstname,
    lastname,
    userType,
    userId,
    email,
    name: data.name || `${firstname} ${lastname}`.trim(),
    type,
  };
}

function getChatStorageKey(user) {
  if (!user) return "";
  const data = user.data || user;
  const userId = data.id ?? data.userId ?? data.email ?? data.username;
  return userId ? `${CHAT_STORAGE_PREFIX}:${encodeURIComponent(String(userId))}` : "";
}

function getPreviousMessages(chatMessages) {
  const normalizedMessages = chatMessages
    .filter((message) => {
      const text = message.text?.trim();
      if (!text) return false;
      return !(message.role === "assistant" && text === INITIAL_ASSISTANT_TEXT);
    })
    .map((message) => ({
      role: message.role,
      content: message.text.trim(),
    }));

  const dedupedMessages = normalizedMessages.filter((message, index, list) => {
    const previous = list[index - 1];
    return !(previous && previous.role === message.role && previous.content === message.content);
  });

  return dedupedMessages.slice(-PREVIOUS_MESSAGE_LIMIT);
}

// eslint-disable-next-line no-unused-vars
function formatMessageText(text) {
  // Replace newlines with <br />
  let formatted = text.replace(/\n/g, "<br />");

  // Convert markdown bold **text** to <strong>text</strong>
  formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

  // Convert markdown links [text](url) to clickable HTML links
  formatted = formatted.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (match, linkText, url) => {
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="font-weight: bold; text-decoration: underline; color: inherit; cursor: pointer;">${linkText}</a>`;
  });

  // Regular expression to match plain URLs (but not those already in HTML tags)
  // Match URLs that are not already wrapped in <a> tags
  const urlRegex = /(?<!href=")(?<![>\w])(https?:\/\/[^\s<>"']+)(?![^<]*<\/a>)/g;

  // Replace plain URLs with clickable links that are bold
  formatted = formatted.replace(urlRegex, (url) => {
    // Remove trailing punctuation that might have been captured
    url = url.replace(/[.,;:!?)]+$/, '');
    return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="font-weight: bold; text-decoration: underline; color: inherit; cursor: pointer;">${url}</a>`;
  });

  // Convert markdown bullet lists - bullet with optional bold text
  formatted = formatted.replace(/- /g, '• ');

  return formatted;
}

function linkifyPlainUrls(text) {
  const markdownLinkPattern = /(\[[^\]\n]+\]\(https?:\/\/[^\s)]+\))/g;
  const markdownLinkOnlyPattern = /^\[[^\]\n]+\]\(https?:\/\/[^\s)]+\)$/;
  const plainUrlPattern = /(^|[\s(])((?:https?:\/\/)[^\s<>()\]]+)/g;

  return text
    .split(markdownLinkPattern)
    .map((part) => {
      if (markdownLinkOnlyPattern.test(part)) return part;
      // ✅ Add this: skip parts that contain no plain URLs (already fully markdown)
      if (!/(https?:\/\/)/.test(part)) return part;

      return part.replace(plainUrlPattern, (match, prefix, url) => {
        let cleanUrl = url;
        let trailing = "";
        while (/[.,;:!?)]$/.test(cleanUrl)) {
          trailing = cleanUrl.slice(-1) + trailing;
          cleanUrl = cleanUrl.slice(0, -1);
        }
        return `${prefix}[${cleanUrl}](${cleanUrl})${trailing}`;
      });
    })
    .join("");
}

function normalizeMarkdownText(text) {
  return linkifyPlainUrls(String(text || "").replace(/\\n/g, "\n"));
}

function getAskDetailMessageText(msg) {
  const text = String(msg.text || "");
  const shouldShowDetailPrompt =
    msg.askDetail === true &&
    ["form", "submitting", "submitted"].includes(msg.detailState);

  if (!shouldShowDetailPrompt) return text;

  return text.replace(DETAIL_PROMPT_TEXT, DETAIL_FORM_PROMPT_TEXT);
}

function formatDetailString(detailForm) {
  return [
    `Name: ${detailForm.name.trim()}`,
    `Email: ${detailForm.email.trim()}`,
    `Phone Number: ${detailForm.phoneNumber.trim()}`,
    `Message: ${detailForm.message.trim()}`,
  ].join(", ");
}

function getDetailStepIndex(message) {
  const step = Number(message?.detailStep || 0);
  return Math.min(Math.max(step, 0), DETAIL_FORM_STEPS.length - 1);
}

function getDetailFieldError(field, value) {
  const trimmedValue = String(value || "").trim();

  if (!trimmedValue) return "This field is required";

  if (field === "email") {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailPattern.test(trimmedValue) ? "" : "Please enter a valid email";
  }

  if (field === "phoneNumber") {
    const phonePattern = /^[0-9+\-().\s]+$/;
    const digitCount = (trimmedValue.match(/\d/g) || []).length;
    return phonePattern.test(trimmedValue) && digitCount >= 7
      ? ""
      : "Please enter a valid phone number";
  }

  return "";
}

function getDetailFormErrors(detailForm) {
  return DETAIL_FORM_STEPS.reduce((errors, step) => {
    const error = getDetailFieldError(step.field, detailForm[step.field]);
    return error ? { ...errors, [step.field]: error } : errors;
  }, {});
}

function Message({
  msg,
  primaryColor,
  messageIndex,
  loading,
  onAskDetailYes,
  onAskDetailNo,
  onDetailChange,
  onDetailSubmit,
}) {
  const isUser = msg.role === "user";
  const shouldRenderMarkdown = !isUser && msg.isBackendAnswer === true;
  const displayText = getAskDetailMessageText(msg);
  const processedText = shouldRenderMarkdown ? normalizeMarkdownText(displayText) : displayText;
  const showAskDetailActions = msg.askDetail === true && msg.detailState === "prompt";
  const showDetailForm =
    msg.askDetail === true &&
    (msg.detailState === "form" || msg.detailState === "submitting");
  const detailForm = { ...DETAIL_FORM_INITIAL_STATE, ...(msg.detailForm || {}) };
  const detailFormDisabled = loading || msg.detailState === "submitting";
  const detailStepIndex = getDetailStepIndex(msg);
  const detailStep = DETAIL_FORM_STEPS[detailStepIndex];
  const detailValue = detailForm[detailStep.field] || "";
  const detailError = msg.detailErrors?.[detailStep.field] || "";
  const isFinalDetailStep = detailStepIndex === DETAIL_FORM_STEPS.length - 1;
  const activeDetailFieldRef = useRef(null);

  useEffect(() => {
    if (!showDetailForm || detailFormDisabled) return undefined;

    const focusTimer = window.setTimeout(() => {
      activeDetailFieldRef.current?.focus();
    }, 80);

    return () => window.clearTimeout(focusTimer);
  }, [
    showDetailForm,
    detailFormDisabled,
    detailStepIndex,
    msg.detailState,
    msg.detailFocusKey,
  ]);

  return (
    <div className={`ecw-msg-row ${isUser ? "ecw-msg-row--user" : ""} ecw-fade-in`}>
      {!isUser && (
        <div className="ecw-msg-avatar">
          <BotIcon />
        </div>
      )}

      <div
        className={`ecw-bubble ${isUser ? "ecw-bubble--user" : "ecw-bubble--bot"} ${
          showDetailForm ? "ecw-bubble--detail" : ""
        }`}
        style={isUser ? { background: primaryColor } : {}}
      >
        {shouldRenderMarkdown ? (
          <div className="ecw-markdown-content" style={{ wordBreak: "break-word" }}>
            <ReactMarkdown components={MARKDOWN_COMPONENTS}>
              {processedText}
            </ReactMarkdown>
          </div>
        ) : (
          <div className="ecw-plain-message">{processedText}</div>
        )}

        {showAskDetailActions && (
          <div className="ecw-detail-actions">
            <button
              type="button"
              className="ecw-detail-btn ecw-detail-btn--primary"
              onClick={() => onAskDetailYes(messageIndex)}
              disabled={loading}
            >
              Yes
            </button>
            <button
              type="button"
              className="ecw-detail-btn"
              onClick={() => onAskDetailNo(messageIndex)}
              disabled={loading}
            >
              No
            </button>
          </div>
        )}

        {showDetailForm && (
          <form
            className="ecw-detail-form"
            onSubmit={(e) => onDetailSubmit(e, messageIndex)}
          >
            <div className="ecw-detail-step-meta">
              Step {detailStepIndex + 1} of {DETAIL_FORM_STEPS.length}
            </div>
            <label className="ecw-detail-field">
              <span>{detailStep.prompt}</span>
              {detailStep.inputType === "textarea" ? (
                <textarea
                  ref={activeDetailFieldRef}
                  rows={3}
                  value={detailValue}
                  onChange={(e) =>
                    onDetailChange(messageIndex, detailStep.field, e.target.value)
                  }
                  disabled={detailFormDisabled}
                  autoComplete={detailStep.autoComplete}
                  placeholder={detailStep.placeholder}
                  aria-invalid={Boolean(detailError)}
                />
              ) : (
                <input
                  ref={activeDetailFieldRef}
                  type={detailStep.inputType}
                  value={detailValue}
                  onChange={(e) =>
                    onDetailChange(messageIndex, detailStep.field, e.target.value)
                  }
                  disabled={detailFormDisabled}
                  autoComplete={detailStep.autoComplete}
                  placeholder={detailStep.placeholder}
                  aria-invalid={Boolean(detailError)}
                />
              )}
            </label>
            {detailError && <div className="ecw-detail-error">{detailError}</div>}
            <button
              type="submit"
              className="ecw-detail-submit ecw-detail-submit--step"
              disabled={detailFormDisabled}
              style={{ background: primaryColor }}
            >
              {msg.detailState === "submitting"
                ? "Submitting..."
                : isFinalDetailStep
                  ? "Submit"
                  : "Next"}
              {msg.detailState !== "submitting" && !isFinalDetailStep && <NextIcon />}
            </button>
          </form>
        )}

        <div className="ecw-bubble-time">{msg.time}</div>
      </div>
    </div>
  );
}

function getInitialMessages() {
  return [
    {
      role: "assistant",
      text: INITIAL_ASSISTANT_TEXT,
      time: now(),
    },
  ];
}

export default function AIChatWidget({
  primaryColor = "#3d7a36",
  companyName = "Evergreen Financial",
  position = "bottom-right",
}) {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [messages, setMessages] = useState(getInitialMessages());
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const detailSubmittingRef = useRef(new Set());
  const isHeaderContactDisabled = loading;
  const user = useSelector((state) => state.user.user);
  const isLoggedIn = useSelector((state) => state.user.isLoggedIn);
  const [sessionId, setSessionId] = useState("");
  const [chatStorageReady, setChatStorageReady] = useState(false);
  const [loadedChatStorageKey, setLoadedChatStorageKey] = useState("");
  const activeChatStorageKeyRef = useRef("");
  const chatStorageKey = isLoggedIn ? getChatStorageKey(user) : "";
  const isRight = position !== "bottom-left";
// console.log("User from Redux:", user);
// console.log("AI API Base URL:", process.env.REACT_APP_AI_API_BASE_URL);

  // Keep chat history only for logged-in users. Guest chat stays in memory only.
  useEffect(() => {
    setChatStorageReady(false);
    setLoadedChatStorageKey("");

    if (!isLoggedIn || !chatStorageKey) {
      const activeChatStorageKey = activeChatStorageKeyRef.current;
      if (activeChatStorageKey) {
        localStorage.removeItem(`${activeChatStorageKey}:messages`);
        localStorage.removeItem(`${activeChatStorageKey}:sessionId`);
        activeChatStorageKeyRef.current = "";
      }

      setMessages(getInitialMessages());
      setSessionId("");
      setInput("");
      setUnread(0);
      localStorage.removeItem(LEGACY_CHAT_MESSAGES_KEY);
      localStorage.removeItem(LEGACY_CHAT_SESSION_ID_KEY);
      setLoadedChatStorageKey("");
      setChatStorageReady(true);
      return;
    }

    const messagesKey = `${chatStorageKey}:messages`;
    const sessionKey = `${chatStorageKey}:sessionId`;
    const savedMessages =
      localStorage.getItem(messagesKey) ||
      localStorage.getItem(LEGACY_CHAT_MESSAGES_KEY);
    const savedSessionId =
      localStorage.getItem(sessionKey) ||
      localStorage.getItem(LEGACY_CHAT_SESSION_ID_KEY);

    if (savedMessages) {
      try {
        setMessages(JSON.parse(savedMessages));
      } catch (e) {
        console.error('Failed to parse saved messages:', e);
        setMessages(getInitialMessages());
      }
    } else {
      setMessages(getInitialMessages());
    }

    setSessionId(savedSessionId || "");
    setInput("");
    setUnread(0);
    localStorage.removeItem(LEGACY_CHAT_MESSAGES_KEY);
    localStorage.removeItem(LEGACY_CHAT_SESSION_ID_KEY);
    activeChatStorageKeyRef.current = chatStorageKey;
    setLoadedChatStorageKey(chatStorageKey);
    setChatStorageReady(true);
  }, [isLoggedIn, chatStorageKey]);

  // Save messages and sessionId to localStorage only for logged-in users.
  useEffect(() => {
    if (
      !isLoggedIn ||
      !chatStorageKey ||
      !chatStorageReady ||
      loadedChatStorageKey !== chatStorageKey
    ) {
      return;
    }
    localStorage.setItem(`${chatStorageKey}:messages`, JSON.stringify(messages));
  }, [messages, isLoggedIn, chatStorageKey, chatStorageReady, loadedChatStorageKey]);

  useEffect(() => {
    if (
      !isLoggedIn ||
      !chatStorageKey ||
      !chatStorageReady ||
      loadedChatStorageKey !== chatStorageKey
    ) {
      return;
    }
    const sessionKey = `${chatStorageKey}:sessionId`;
    if (sessionId) {
      localStorage.setItem(sessionKey, sessionId);
    } else {
      localStorage.removeItem(sessionKey);
    }
  }, [sessionId, isLoggedIn, chatStorageKey, chatStorageReady, loadedChatStorageKey]);

  useEffect(() => {
    if (open) setUnread(0);
  }, [open]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading, open]);

  function updateAskDetailMessage(messageIndex, updates) {
    setMessages((previousMessages) =>
      previousMessages.map((message, index) =>
        index === messageIndex ? { ...message, ...updates } : message
      )
    );
  }

  function addAssistantMessage(text) {
    setMessages((previousMessages) => {
      const lastMessage = previousMessages[previousMessages.length - 1];
      if (lastMessage?.role === "assistant" && lastMessage.text === text) {
        return previousMessages;
      }

      return [
        ...previousMessages,
        {
          role: "assistant",
          text,
          time: now(),
        },
      ];
    });
  }

  function handleAskDetailYes(messageIndex) {
    if (loading) return;

    updateAskDetailMessage(messageIndex, {
      detailState: "form",
      detailForm: { ...DETAIL_FORM_INITIAL_STATE },
      detailStep: 0,
      detailErrors: {},
      detailFocusKey: Date.now(),
    });
  }

  function handleAskDetailNo(messageIndex) {
    if (loading) return;

    const detailMessage = messages[messageIndex];
    updateAskDetailMessage(messageIndex, { detailState: "declined" });
    sendMessage("No", {
      askDetail: true,
      sessionIdOverride: detailMessage?.sessionId,
    });
  }

  function handleDetailChange(messageIndex, field, value) {
    setMessages((previousMessages) =>
      previousMessages.map((message, index) => {
        if (index !== messageIndex) return message;

        return {
          ...message,
          detailForm: {
            ...DETAIL_FORM_INITIAL_STATE,
            ...(message.detailForm || {}),
            [field]: value,
          },
          detailErrors: {
            ...(message.detailErrors || {}),
            [field]: message.detailErrors?.[field]
              ? getDetailFieldError(field, value)
              : "",
          },
        };
      })
    );
  }

  function handleDetailSubmit(e, messageIndex) {
    e.preventDefault();

    if (loading || detailSubmittingRef.current.has(messageIndex)) return;

    const detailMessage = messages[messageIndex];
    if (!detailMessage || detailMessage.detailState === "submitting") return;

    const detailForm = { ...DETAIL_FORM_INITIAL_STATE, ...(detailMessage.detailForm || {}) };
    const detailStepIndex = getDetailStepIndex(detailMessage);
    const detailStep = DETAIL_FORM_STEPS[detailStepIndex];
    const currentError = getDetailFieldError(detailStep.field, detailForm[detailStep.field]);

    if (currentError) {
      updateAskDetailMessage(messageIndex, {
        detailErrors: {
          ...(detailMessage.detailErrors || {}),
          [detailStep.field]: currentError,
        },
        detailFocusKey: Date.now(),
      });
      return;
    }

    if (detailStepIndex < DETAIL_FORM_STEPS.length - 1) {
      updateAskDetailMessage(messageIndex, {
        detailStep: detailStepIndex + 1,
        detailErrors: {
          ...(detailMessage.detailErrors || {}),
          [detailStep.field]: "",
        },
        detailFocusKey: Date.now(),
      });
      return;
    }

    const detailErrors = getDetailFormErrors(detailForm);
    if (Object.keys(detailErrors).length > 0) {
      const firstErrorStepIndex = DETAIL_FORM_STEPS.findIndex(
        (step) => detailErrors[step.field]
      );
      updateAskDetailMessage(messageIndex, {
        detailStep: firstErrorStepIndex === -1 ? detailStepIndex : firstErrorStepIndex,
        detailErrors,
        detailFocusKey: Date.now(),
      });
      return;
    }

    const name = detailForm.name.trim();
    const email = detailForm.email.trim();
    const phoneNumber = detailForm.phoneNumber.trim();
    const message = detailForm.message.trim();

    detailSubmittingRef.current.add(messageIndex);
    updateAskDetailMessage(messageIndex, { detailState: "submitting" });

    Promise.resolve(sendMessage(
      detailMessage.contactFlowAction === "start" ? "Submit contact request" : "Yes",
      {
        askDetail: true,
        contactFlowAction: detailMessage.contactFlowAction === "start" ? "submit" : undefined,
        detail: formatDetailString({ name, email, phoneNumber, message }),
        displayText: CONTACT_SUBMISSION_DISPLAY_TEXT,
        sessionIdOverride: detailMessage.sessionId,
      }
    )).then((sent) => {
      if (!sent) {
        detailSubmittingRef.current.delete(messageIndex);
      }

      updateAskDetailMessage(messageIndex, {
        detailState: sent ? "submitted" : "form",
        detailErrors: sent ? {} : detailMessage.detailErrors || {},
      });
    });
  }

  function openExistingDetailFlow() {
    const submittedDetailIndex = messages.findIndex(
      (message) => message.askDetail === true && message.detailState === "submitted"
    );

    if (submittedDetailIndex !== -1) {
      addAssistantMessage(CONTACT_FORM_SUBMITTED_TEXT);
      return true;
    }

    const existingDetailIndex = messages.findIndex(
      (message) =>
        message.askDetail === true &&
        ["prompt", "form", "submitting"].includes(message.detailState)
    );

    if (existingDetailIndex === -1) return false;

    const existingMessage = messages[existingDetailIndex];
    if (existingMessage.detailState !== "submitting") {
      updateAskDetailMessage(existingDetailIndex, {
        detailState: "form",
        detailForm: {
          ...DETAIL_FORM_INITIAL_STATE,
          ...(existingMessage.detailForm || {}),
        },
        detailStep: getDetailStepIndex(existingMessage),
        detailFocusKey: Date.now(),
      });
    }

    addAssistantMessage(CONTACT_FORM_ALREADY_OPEN_TEXT);
    return true;
  }

  function handleHeaderContactClick() {
    setOpen(true);
    setUnread(0);

    if (loading) return;
    if (openExistingDetailFlow()) return;

    sendMessage(HEADER_CONTACT_CTA_TEXT, {
      askDetail: true,
      contactFlowAction: "start",
      openDetailFormOnResponse: true,
    });
  }

  async function sendMessage(text, options = {}) {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    const displayText = (options.displayText || msg).trim();

    setMessages((p) => [...p, { role: "user", text: displayText, time: now() }]);
    setInput("");
    setLoading(true);

    try {
      const previousMessages = getPreviousMessages(messages);
      const requestSessionId = !isLoggedIn
        ? options.sessionIdOverride || sessionId
        : "";

      const payload = {
        question: msg,
        currentQuestion: msg,
        previousMessages,
        userDetail: {},
      };

      if (!isLoggedIn) {
        payload.sessionId = requestSessionId;
      }

      if (options.askDetail === true) {
        payload.askDetail = true;
        payload.detail = options.detail;
      }

      if (options.contactFlowAction) {
        payload.contactFlowAction = options.contactFlowAction;
      }

      if (isLoggedIn) {
        const userDetail = formatUserDetail(user);
        if (userDetail) payload.userDetail = userDetail;
      }

      const res = await fetch(`${process.env.REACT_APP_AI_API_BASE_URL}api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!isLoggedIn && data.sessionId && data.sessionId !== requestSessionId) {
        setSessionId(data.sessionId);
      }

      const shouldAskDetail =
        options.openDetailFormOnResponse === true ||
        (data.askDetail === true && options.contactFlowAction !== "submit");
      const fallbackAnswer =
        options.contactFlowAction === "submit"
          ? CONTACT_SUBMISSION_SUCCESS_TEXT
          : options.openDetailFormOnResponse
            ? DETAIL_FORM_PROMPT_TEXT
            : "Sorry, something went wrong.";

      setMessages((p) => [
        ...p,
        {
          role: "assistant",
          text: data.answer || fallbackAnswer,
          time: now(),
          isBackendAnswer: true,
          askDetail: shouldAskDetail,
          detailState: shouldAskDetail
            ? options.openDetailFormOnResponse
              ? "form"
              : "prompt"
            : undefined,
          detailForm: shouldAskDetail ? { ...DETAIL_FORM_INITIAL_STATE } : undefined,
          detailStep: shouldAskDetail ? 0 : undefined,
          detailErrors: shouldAskDetail ? {} : undefined,
          detailFocusKey: shouldAskDetail ? Date.now() : undefined,
          contactFlowAction: options.contactFlowAction === "start" ? "start" : undefined,
          sessionId: !isLoggedIn ? data.sessionId || requestSessionId : undefined,
        },
      ]);

      if (!open) setUnread((u) => u + 1);
      return true;
    } catch {
      setMessages((p) => [
        ...p,
        {
          role: "assistant",
          text: "Sorry, I couldn't connect. Please try again.",
          time: now(),
        },
      ]);
      return false;
    } finally {
      setLoading(false);
    }
  }

  function handleKey(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  }
// console.log("user<<<<", user)
if(user?.data?.type === "Admin" || user?.data?.type === "Atty"){
  return null;
}
  return (
    <div className="ecw-widget">
      {/* Panel */}
      <div
        className={`ecw-panel ${open ? "ecw-panel--open" : ""}`}
        style={{ [isRight ? "right" : "left"]: 24 }}
      >
        {/* Header */}
        <div
          className="ecw-header"
          style={{
            background: `linear-gradient(135deg, ${shadeColor(primaryColor, -18)}, ${primaryColor})`,
          }}
        >
          <div className="ecw-header-main">
            <div className="ecw-header-avatar">
              <BotIcon />
            </div>

            <div className="ecw-header-text">
              <div className="ecw-header-name">{companyName}</div>
              <div className="ecw-header-status">
                <span className="ecw-status-dot" />
                <span className="ecw-status-label">
                  Advisor available ·
                </span>
              </div>
            </div>

            <div className="ecw-header-actions">
              <button
                className="ecw-icon-btn"
                title="Close"
                onClick={() => setOpen(false)}
              >
                <CloseIcon />
              </button>
            </div>
            
          </div>

          {/* <div className="ecw-header-strip">
            <button
              type="button"
              className="ecw-header-cta"
              onClick={isHeaderContactDisabled ? undefined : handleHeaderContactClick}
              disabled={isHeaderContactDisabled}
              aria-disabled={isHeaderContactDisabled}
              aria-busy={isHeaderContactDisabled}
              title={
                isHeaderContactDisabled
                  ? "Support contact is temporarily unavailable while the assistant responds."
                  : "Open the support contact form. A representative will contact you."
              }
              aria-label={
                isHeaderContactDisabled
                  ? "Support contact form temporarily unavailable while the assistant responds"
                  : "Open the support contact form"
              }
            >
              <span className="ecw-header-cta-copy">
                <span className="ecw-header-cta-title">
                  {HEADER_CONTACT_CTA_TEXT}
                </span>
                <span className="ecw-header-cta-helper">
                  {HEADER_CONTACT_CTA_HELPER_TEXT}
                </span>
              </span>
              <span className="ecw-header-cta-icon" aria-hidden="true">
                <NextIcon />
              </span>
            </button>
          </div> */}
          <div className="ecw-header-strip" style={{textAlign:'center'}}>
            Financial Counseling · Secure Conversation
            </div>
          
        </div>
        

        {/* Messages */}
        <div className="ecw-feed">
          <div className="ecw-date-divider">Today, {now()}</div>

          {messages.map((m, i) => (
            <Message
              key={i}
              msg={m}
              messageIndex={i}
              primaryColor={primaryColor}
              loading={loading}
              onAskDetailYes={handleAskDetailYes}
              onAskDetailNo={handleAskDetailNo}
              onDetailChange={handleDetailChange}
              onDetailSubmit={handleDetailSubmit}
            />
          ))}

          {loading && (
            <div className="ecw-msg-row">
              <div className="ecw-msg-avatar">
                <BotIcon />
              </div>
              <div className="ecw-bubble ecw-bubble--bot ecw-typing">
                <span className="ecw-dot" />
                <span className="ecw-dot" />
                <span className="ecw-dot" />
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* Suggestions */}
        {messages.length <= 2 && (
          <div className="ecw-chips">
            {SUGGESTIONS.map((s) => (
              <button key={s} className="ecw-chip" onClick={() => sendMessage(s)}>
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Input */}
        <div className="ecw-input-area">
          <div className="ecw-input-row">
            <textarea
              rows={1}
              value={input}
              onChange={(e) => {
                setInput(e.target.value);
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
              }}
              onKeyDown={handleKey}
              className="ecw-textarea"
              placeholder="Type your message…"
              style={{ "--focus-color": primaryColor }}
            />

            <button
              className="ecw-send-btn"
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              style={{ background: primaryColor }}
            >
              <SendIcon />
            </button>
          </div>

          <div className="ecw-input-footer">
            Powered by <strong>{companyName} AI</strong> · 
          </div>
        </div>
      </div>

      {/* FAB */}
      <button
        className={`ecw-fab ${open ? "ecw-fab--open" : ""}`}
        style={{
          [isRight ? "right" : "left"]: 24,
          background: `linear-gradient(135deg, ${shadeColor(primaryColor, -18)}, ${primaryColor})`,
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="ecw-fab-icon ecw-fab-chat">
          <ChatIcon />
        </span>
        <span className="ecw-fab-icon ecw-fab-close">
          <CloseIcon />
        </span>

        {unread > 0 && !open && (
          <span className="ecw-badge">{unread > 9 ? "9+" : unread}</span>
        )}
      </button>
    </div>
  );
}

function shadeColor(hex, pct) {
  const num = parseInt(hex.replace("#", ""), 16);
  const clamp = (v) => Math.min(255, Math.max(0, v));
  const r = clamp((num >> 16) + pct);
  const g = clamp(((num >> 8) & 0xff) + pct);
  const b = clamp((num & 0xff) + pct);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}
